import { QuestionnaireForm } from "@/components/questionnaire/QuestionnaireForm.tsx";
import { QuestionnaireProvider } from "@/components/questionnaire/QuestionnaireProvider.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { SurveyCompleted } from "@/pages/employees/surveys/components/SurveyCompleted.tsx";
import { SurveyStart } from "@/pages/employees/surveys/components/SurveyStart.tsx";
import { useEmployeeSurveyById } from "@/queries/employees/use-employee-survey.query.ts";
import { useEmployeeDetails, useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";

import { PortalAccessMode } from "@/types/employees/employee.types.ts";
import { ApiFormParticipantStatus, ApiFormType } from "@/types/forms/form.types.ts";
import { ApiSurveyStatus } from "@/types/survey.types.ts";
import { Button, FullScreenPage, Stack } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useEffectOnce } from "react-use";
import { SurveyTargetEmployee } from "@/pages/employees/surveys/components/SurveyTargetEmployee.tsx";

export const SurveyRespond = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: employeeMe } = useEmployeeMe();

  const { data: survey } = useEmployeeSurveyById({
    employeeId: employeeMe?.id || 0,
    surveyId: Number(id),
    enabled: !!employeeMe,
  });
  const targetEmployeeId = survey?.target_employee?.id;
  const isRestrictedPortal =
    !!employeeMe && employeeMe.portal_access_mode !== PortalAccessMode.full;
  const shouldLoadTargetEmployee =
    !isRestrictedPortal &&
    !!targetEmployeeId &&
    survey?.status !== ApiSurveyStatus.completed &&
    survey?.participant_status !== ApiFormParticipantStatus.completed &&
    survey?.participant_status !== ApiFormParticipantStatus.invited;
  const {
    data: targetEmployee,
    isPending: isTargetEmployeePending,
    isError: isTargetEmployeeError,
  } = useEmployeeDetails(targetEmployeeId, shouldLoadTargetEmployee);

  useTitle(survey ? survey.title : undefined);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.surveys.responseFormViewed, {
      [analyticProperties.id]: id,
    });
  });

  if (!survey || (shouldLoadTargetEmployee && isTargetEmployeePending && !isTargetEmployeeError)) {
    return <FullScreenPage isCenterVertical={false}></FullScreenPage>;
  }

  if (
    survey.status == ApiSurveyStatus.completed ||
    survey.participant_status == ApiFormParticipantStatus.completed
  ) {
    return <SurveyCompleted survey={survey} />;
  }

  if (survey.participant_status == ApiFormParticipantStatus.invited) {
    return <SurveyStart survey={survey} />;
  }

  return (
    <>
      <FullScreenPage
        title={survey.title}
        backSlot={
          <Button
            variant={"secondary"}
            size={"lg"}
            onClick={() => {
              navigate("/surveys");
            }}
          >
            {t("common:exit")}
          </Button>
        }
        isCenterVertical={false}
      >
        <QuestionnaireProvider
          type={ApiFormType.survey}
          id={id}
          isAnonymous={survey.is_anonymous}
          targetEmployee={targetEmployee ?? survey.target_employee ?? undefined}
          onAfterSubmit={() => {
            analyticsService.trackEvent(analyticEvents.surveys.responseCompleted, {
              [analyticProperties.id]: id,
            });
          }}
        >
          <QuestionnaireForm />
        </QuestionnaireProvider>
        {survey.target_employee && !isRestrictedPortal && (
          <Stack className={"lg:fixed lg:pt-45 pt-10 right-0 top-0 pr-6 pb-4"} gap={"xl"}>
            <SurveyTargetEmployee employee={survey.target_employee} />
          </Stack>
        )}
      </FullScreenPage>
    </>
  );
};
